import { Logger } from '@saviynt/common';
import { type FormikTouched } from 'formik';

import {
  EXPRESSIONS_TYPES,
  LAYOUT_TYPES,
  REQUIRED_EXPRESSION_TYPES,
  VISIBILITY_EXPRESSION_TYPES,
} from '../../../../shared/constants/global';
import {
  type Expression,
  type FieldElement,
  type FormValues,
} from '../../../../shared/types';
import {
  evaluateDateValidation,
  evaluateExpression,
} from '../../../ExpressionBuilder/utils';
import { type EntityInitialData } from '../../data-store';
import { shouldDaDefaultVisibleWhenHidden } from '../../utils/daVisibilityHelpers';

import { type ExpressionContext } from './types';

/**
 * Check if field is currently visible based on visibility expressions.
 * Used internally by isFieldConditionallyRequired; not part of public API.
 */
const isFieldCurrentlyVisible = (
  field: FieldElement,
  values: FormValues,
  touched: FormikTouched<FormValues>,
  entityData: EntityInitialData | null,
  isDaForm = false
): boolean => {
  const visibilityExpression =
    'expressions' in field
      ? field.expressions?.find(
          (expr: Expression) => expr.type === EXPRESSIONS_TYPES.VISIBILITY
        )
      : undefined;

  if (!visibilityExpression?.enabled) {
    return true;
  }

  if (touched[field.fieldName]) {
    return true;
  }

  const hideValue = visibilityExpression.value.find(
    (v: { kind: string; selected: boolean; value?: string }) =>
      v.kind === VISIBILITY_EXPRESSION_TYPES.HIDE && v.selected
  );

  if (hideValue?.value) {
    const result = evaluateExpression(hideValue.value, {
      values,
      touched,
      entityData,
    });

    return !result;
  }

  const showValue = visibilityExpression.value.find(
    (v: { kind: string; selected: boolean; value?: string }) =>
      v.kind === VISIBILITY_EXPRESSION_TYPES.SHOW && v.selected
  );

  if (showValue?.value) {
    const result = evaluateExpression(showValue.value, {
      values,
      touched,
      entityData,
    });

    if (
      !result &&
      shouldDaDefaultVisibleWhenHidden(
        visibilityExpression,
        { values, touched, entityData },
        isDaForm
      )
    ) {
      return true;
    }

    return result;
  }

  return true;
};

/**
 * Check if field is conditionally required based on required expressions
 */
export const isFieldConditionallyRequired = (
  field: FieldElement,
  values: FormValues,
  touched: FormikTouched<FormValues>,
  entityData: EntityInitialData | null,
  isDaForm = false
): boolean => {
  if (!isFieldCurrentlyVisible(field, values, touched, entityData, isDaForm)) {
    return false;
  }

  if (
    field.type === LAYOUT_TYPES.FIELD &&
    'isReadOnly' in field &&
    field.isReadOnly
  ) {
    return false;
  }

  const requiredExpression =
    'expressions' in field
      ? field.expressions?.find(
          (expr: Expression) => expr.type === EXPRESSIONS_TYPES.REQUIRED
        )
      : undefined;

  if (!requiredExpression?.enabled) {
    return false;
  }

  const requiredValue = requiredExpression.value.find(
    (v: { kind: string; selected: boolean; value?: string }) =>
      v.kind === REQUIRED_EXPRESSION_TYPES.REQUIRED && v.selected
  );

  if (requiredValue?.value) {
    return evaluateExpression(requiredValue.value, {
      values,
      touched,
      entityData,
    });
  }

  const notRequiredValue = requiredExpression.value.find(
    (v: { kind: string; selected: boolean; value?: string }) =>
      v.kind === REQUIRED_EXPRESSION_TYPES.NOT_REQUIRED && v.selected
  );

  if (notRequiredValue?.value) {
    return !evaluateExpression(notRequiredValue.value, {
      values,
      touched,
      entityData,
    });
  }

  return false;
};

/**
 * Validate number field expression
 */
export const validateNumberExpression = (
  value: number | null | undefined,
  ruleValue: string,
  context: ExpressionContext
): boolean => {
  if (!value && value !== 0) {
    return true;
  }

  try {
    return evaluateExpression(ruleValue, {
      values: context.values,
      touched: context.touched ?? {},
      entityData: context.entityData ?? null,
    });
  } catch {
    return true;
  }
};

/**
 * Validate number field regex
 */
export const validateNumberRegex = (
  value: number | null | undefined,
  regexPattern: RegExp
): boolean => {
  if (value === null || value === undefined) {
    return true;
  }

  return regexPattern.test(String(value));
};

/**
 * Validate date field expression
 */
export const validateDateExpression = (
  value: Date | null | undefined,
  ruleValue: string,
  fieldName: string,
  currentValues: FormValues,
  entityData: EntityInitialData | null
): boolean => {
  if (!value) {
    return true;
  }

  const context = {
    ...currentValues,
    [fieldName]: value,
  };

  try {
    const result = evaluateDateValidation(ruleValue, {
      values: context,
      entityData,
    });

    return Boolean(result);
  } catch {
    return false;
  }
};

/**
 * Validate string expression
 */
export const validateStringExpression = (
  value: string | undefined,
  ruleValue: string,
  fieldName: string,
  context: ExpressionContext
): boolean => {
  if (!value || value === '') {
    return true;
  }

  const updatedContext = {
    values: {
      ...context.values,
      [fieldName]: value,
    },
    touched: context.touched ?? {},
    entityData: context.entityData ?? null,
  };

  try {
    const result = evaluateExpression(ruleValue, updatedContext);

    return Boolean(result);
  } catch (error) {
    Logger.log(error);

    return false;
  }
};
